/*
 * РАЗДЕЛ «КУРСЫ» — список уроков и прогресс ученика.
 *
 * Отсюда ребёнок заходит в урок: карточка → редактор с открытой панелью
 * задания (адрес /main/cad-editor/new?lesson=<id>).
 *
 * Уроки идут строго по порядку, но НЕ запираются на замок: пройденное
 * подсвечено, следующее выделено как «продолжить», а забежать вперёд никто
 * не мешает. Жёсткая блокировка в домашнем обучении только раздражает —
 * ребёнок и так идёт сверху вниз, а если хочет посмотреть, что дальше,
 * пусть смотрит.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { COURSES } from './courses';
import { getCurrentUserId } from '../cloudIO';
import { loadProgress, courseProgress } from './progress';
import cl from './CoursesPage.module.css';


/** Галочка пройденного урока. */
const DoneIcon = () => (
    <svg viewBox="0 0 20 20" width="20" height="20" className={cl.doneIcon}>
        <circle cx="10" cy="10" r="9" fill="currentColor"/>
        <path d="M5.8 10.3l2.8 2.8 5.6-5.6" fill="none" stroke="var(--k-accent-ink)"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

/** Номер урока в кружке — для непройденных. */
const NumberBadge = ({ n, next }) => (
    <span className={next ? cl.numNext : cl.num}>{n}</span>
);


const CoursesPage = () => {
    const navigate = useNavigate();
    const userId = getCurrentUserId();
    const progress = loadProgress(userId);

    const openLesson = (lessonId) => {
        navigate(`/main/cad-editor/new?lesson=${encodeURIComponent(lessonId)}`);
    };

    return (
        <div className={cl.page}>
            <header className={cl.pageHead}>
                <h1 className={cl.pageTitle}>Курсы 3D-моделирования</h1>
                <p className={cl.pageSub}>
                    От первого эскиза до готовой детали под печать. Каждый урок —
                    вещь, которую можно распечатать и взять в руки.
                </p>
            </header>

            {COURSES.map((course) => {
                const stat = courseProgress(userId, course);
                // Следующий непройденный урок — его выделяем как «продолжить».
                const nextIndex = course.lessons.findIndex((l) => !progress[l.id]?.done);

                return (
                    <section key={course.id} className={cl.course}>
                        <div className={cl.courseHead}>
                            <div>
                                <h2 className={cl.courseTitle}>{course.title}</h2>
                                <p className={cl.courseSub}>{course.subtitle}</p>
                            </div>
                            <div className={cl.courseStat}>
                                <span className={cl.statNums}>
                                    {stat.done} / {stat.total}
                                </span>
                                <span className={cl.statLabel}>уроков пройдено</span>
                            </div>
                        </div>

                        <p className={cl.courseDesc}>{course.description}</p>

                        {/* Полоса прогресса курса */}
                        <div className={cl.bar}>
                            <div className={cl.barFill}
                                style={{ width: `${(stat.done / stat.total) * 100}%` }}/>
                        </div>

                        <ul className={cl.lessons}>
                            {course.lessons.map((lesson, i) => {
                                const done = !!progress[lesson.id]?.done;
                                const isNext = i === nextIndex;
                                return (
                                    <li key={lesson.id}>
                                        <button type="button"
                                            className={isNext ? cl.lessonNext : cl.lesson}
                                            onClick={() => openLesson(lesson.id)}>
                                            <span className={cl.lessonMark}>
                                                {done ? <DoneIcon/>
                                                    : <NumberBadge n={i + 1} next={isNext}/>}
                                            </span>
                                            <span className={cl.lessonBody}>
                                                <span className={cl.lessonTitle}>
                                                    {lesson.title}
                                                </span>
                                                <span className={cl.lessonGoal}>
                                                    {lesson.goal}
                                                </span>
                                            </span>
                                            {isNext && (
                                                <span className={cl.nextTag}>Продолжить</span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                );
            })}

            <p className={cl.moreNote}>
                Дальше будут курсы про чтение чертежей, посадки и зазоры, тела
                вращения и проектирование по заданию. Они появятся здесь,
                как только будут готовы.
            </p>
        </div>
    );
};

export default CoursesPage;
